import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { Box } from "@mui/material";

import { formatDecimal } from "@aclymatepackages/formatters";

import ScoutRail from "../scout/ScoutRail";
import {
  computeSurveyProgress,
  resolveDefaultTab,
  resolveSurfaceState
} from "../scout/scoutOpeners";
import DashboardCanvas from "../dashboard/DashboardCanvas";
import { usePdfReportDownload } from "../reports/usePdfReportDownload";

import {
  MAX_PENDING_PER_TYPE,
  buildCompletenessSummary,
  buildHotspots,
  buildNextBestAction,
  buildPendingProducts,
  buildPendingSurveys,
  buildScopeSeries,
  buildSourceSeries,
  buildTopSupplierEmissions,
  computeTotalTons,
  countSurveySuppliers,
  deriveSurveyStatus,
  filterEmissionsForYear,
  findSurveyEntry,
  isValidTab,
  parseViewParam,
  resolveBuyerInvite,
  sumTons
} from "./dashboardHelpers";

import {
  useAccountData,
  useCachedDisplayData
} from "../../../helpers/firebase";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import usePermissions from "../../../helpers/hooks/usePermissions";
import { getUrlSearchParams } from "../../../helpers/otherHelpers";
import { useProductsTabContext } from "../../../helpers/components/explorerProductsTab";
import { useCustomersTabContext } from "../../../helpers/components/explorerCustomersTab";
import { useSuppliersTabContext } from "../../../helpers/components/explorerSuppliersTab";

const Dashboard = ({ customers = [], customersLoading = false }) => {
  const [accountData] = useAccountData();
  const {
    vendorSurveys = [],
    revenueYears = [],
    onboardingComplete
  } = accountData;
  const { allEmissions = [] } = useEmissionsContext() || {};
  const [rawTransactions] = useCachedDisplayData("transactions");
  const [productRequests, productRequestsLoading] = useCachedDisplayData(
    "vendor-assigned-product-lifecycle-events"
  );
  const [vendorRelationships] = useCachedDisplayData("vendor-relationships");
  const [offices] = useCachedDisplayData("offices");
  const [dbEmissions] = useCachedDisplayData("emissions");
  const { canShare } = usePermissions();

  const location = useLocation();
  const history = useHistory();

  const mostRecentSurveyYear = vendorSurveys.length
    ? Math.max(...vendorSurveys.map(({ year }) => Number(year)))
    : null;

  const [selectedYear, setSelectedYear] = useState(null);
  const [railCollapsed, setRailCollapsed] = useState(false);

  useEffect(() => {
    if (selectedYear) {
      return;
    }
    if (!mostRecentSurveyYear) {
      return;
    }
    setSelectedYear(mostRecentSurveyYear);
  }, [selectedYear, mostRecentSurveyYear]);

  const activeYear = selectedYear || mostRecentSurveyYear;

  const surveyEmissions = useMemo(
    () => filterEmissionsForYear(allEmissions, activeYear),
    [allEmissions, activeYear]
  );

  const yearSurveyEntry = useMemo(
    () => findSurveyEntry(vendorSurveys, activeYear),
    [vendorSurveys, activeYear]
  );

  const totalTons = useMemo(
    () =>
      computeTotalTons({
        yearSurveyEntry,
        revenueYears,
        activeYear,
        surveyEmissions
      }),
    [yearSurveyEntry, revenueYears, activeYear, surveyEmissions]
  );

  const totalSurveyTons = useMemo(
    () =>
      sumTons(
        surveyEmissions.filter(({ source }) => source === "vendor-survey")
      ),
    [surveyEmissions]
  );

  const scopePieSeries = useMemo(
    () => buildScopeSeries(surveyEmissions),
    [surveyEmissions]
  );

  const sourcePieSeries = useMemo(
    () => buildSourceSeries(surveyEmissions),
    [surveyEmissions]
  );

  const hotspots = useMemo(
    () => buildHotspots(sourcePieSeries, totalTons),
    [sourcePieSeries, totalTons]
  );

  const yearVendorSurveyTransactions = useMemo(
    () => filterEmissionsForYear(rawTransactions, activeYear),
    [rawTransactions, activeYear]
  );

  const topSupplierEmissions = useMemo(
    () => buildTopSupplierEmissions(yearVendorSurveyTransactions),
    [yearVendorSurveyTransactions]
  );

  const surveySupplierCount = useMemo(
    () => countSurveySuppliers(yearVendorSurveyTransactions),
    [yearVendorSurveyTransactions]
  );

  const totalVendorSurveyTons = useMemo(
    () =>
      sumTons(
        (yearVendorSurveyTransactions || []).filter(
          ({ source, description }) =>
            source === "vendor-survey" && (description || "").trim()
        )
      ),
    [yearVendorSurveyTransactions]
  );

  const availableYears = useMemo(
    () => vendorSurveys.map(({ year }) => Number(year)).sort((a, b) => b - a),
    [vendorSurveys]
  );

  const pendingSurveys = useMemo(
    () => buildPendingSurveys(customers),
    [customers]
  );

  const pendingProducts = useMemo(
    () => buildPendingProducts(productRequests),
    [productRequests]
  );

  const viewParam = parseViewParam(location.search);

  const {
    suppliersSummary,
    pendingSupplierShares,
    focusedSupplierRelationshipId
  } = useSuppliersTabContext();

  const defaultTab = useMemo(
    () =>
      resolveDefaultTab({
        pendingRequestsSummary: {
          surveyCount: pendingSurveys.length,
          productCount: pendingProducts.length
        },
        pendingSupplierShares
      }),
    [pendingSurveys.length, pendingProducts.length, pendingSupplierShares]
  );

  const activeTab = isValidTab(viewParam) ? viewParam : defaultTab;
  const isDefaultTabResolverReady =
    !customersLoading && !productRequestsLoading;

  useEffect(() => {
    if (isValidTab(viewParam)) {
      return;
    }
    if (!isDefaultTabResolverReady) {
      return;
    }
    history.replace({
      pathname: "/explorer/dashboard",
      search: `?view=${defaultTab}`
    });
  }, [viewParam, defaultTab, history, isDefaultTabResolverReady]);

  const urlBuyerId = getUrlSearchParams("buyerId");

  const { productsSummary, pendingBomRequests, receivedProductsCount } =
    useProductsTabContext();
  const {
    customersSummary,
    pendingRequests: customersPendingRequests,
    pendingShares: customersPendingShares,
    focusedRelationshipId,
    focusedBuyerName
  } = useCustomersTabContext(customers);

  const surfaceContext = useMemo(() => {
    const surveyAge = yearSurveyEntry?.updatedAt
      ? Date.now() - yearSurveyEntry.updatedAt
      : 0;
    const { buyerInviteId, buyerName } = resolveBuyerInvite(
      urlBuyerId,
      customers
    );
    const onboardingStatus = onboardingComplete?.explorer
      ? "complete"
      : "incomplete";
    const hasProductsInCatalog = (productRequests || []).length > 0;
    const hasVendorsInCatalog = (vendorRelationships || []).length > 0;
    const surveyProgress = yearSurveyEntry
      ? computeSurveyProgress({
          accountData,
          vendorSurveysEntry: yearSurveyEntry,
          hasOffices: (offices || []).length > 0,
          rawTransactions,
          dbEmissions,
          year: activeYear
        })
      : null;
    const completenessSummary = buildCompletenessSummary(surveyProgress);
    const surveyStatus = deriveSurveyStatus(
      vendorSurveys,
      activeYear,
      totalSurveyTons,
      completenessSummary
    );

    const baseContext = {
      year: activeYear,
      activeTab,
      availableYears,
      hasEmissions: totalSurveyTons > 0,
      totalSurveyTons,
      totalTons,
      scopePieSeries,
      sourcePieSeries,
      hotspots,
      completenessSummary,
      pendingRequestsSummary: {
        surveyCount: pendingSurveys.length,
        productCount: pendingProducts.length,
        surveys: pendingSurveys.slice(0, MAX_PENDING_PER_TYPE),
        products: pendingProducts.slice(0, MAX_PENDING_PER_TYPE)
      },
      pendingSupplierShares,
      suppliersSummary,
      focusedSupplierRelationshipId,
      productsSummary,
      pendingBomRequests,
      receivedProductsCount,
      customersSummary,
      pendingRequests: customersPendingRequests,
      pendingShares: customersPendingShares,
      focusedRelationshipId,
      focusedBuyerName,
      onboardingStatus,
      surveyStatus,
      surveyAge,
      surveyProgress,
      hasProductsInCatalog,
      hasVendorsInCatalog,
      arrivedFromBuyerInviteId: buyerInviteId,
      buyerName,
      canShare
    };

    return { ...baseContext, resolvedState: resolveSurfaceState(baseContext) };
  }, [
    canShare,
    accountData,
    activeTab,
    activeYear,
    availableYears,
    customers,
    customersPendingRequests,
    customersPendingShares,
    customersSummary,
    dbEmissions,
    focusedBuyerName,
    focusedRelationshipId,
    focusedSupplierRelationshipId,
    hotspots,
    offices,
    onboardingComplete,
    pendingBomRequests,
    pendingProducts,
    pendingSupplierShares,
    pendingSurveys,
    productRequests,
    productsSummary,
    rawTransactions,
    receivedProductsCount,
    scopePieSeries,
    sourcePieSeries,
    suppliersSummary,
    totalSurveyTons,
    totalTons,
    urlBuyerId,
    vendorRelationships,
    vendorSurveys,
    yearSurveyEntry
  ]);

  const pdfReportDownload = usePdfReportDownload({ triggerPath: "chip" });
  const triggerPdfReportDownload = pdfReportDownload.trigger;

  const onClientAction = useMemo(
    () => ({
      switchYear: (action) => {
        const year = action?.payload?.year;
        if (!availableYears.includes(Number(year))) {
          return;
        }
        setSelectedYear(Number(year));
      },
      downloadPdfReport: () => {
        triggerPdfReportDownload({ year: activeYear });
      },
      showScopeBreakdown: () => ({
        userText: "Show me my scope breakdown.",
        assistantText: `Here's your Scope 1, 2, and 3 breakdown for ${activeYear}. Scope 3 typically dominates for service companies — it's the indirect supply-chain piece.`,
        cards: [
          {
            type: "emissions_chart",
            props: {
              title: `Scope 1, 2, and 3 breakdown — ${activeYear}`,
              emissions: surveyEmissions,
              viewMode: "scopes"
            }
          }
        ]
      }),
      showSourceBreakdown: () => ({
        userText: "Show me my biggest emission sources.",
        assistantText: `Here's where your emissions came from in ${activeYear}.`,
        cards: [
          {
            type: "emissions_chart",
            props: {
              title: `Emissions by source — ${activeYear}`,
              emissions: surveyEmissions,
              viewMode: "subcategories"
            }
          },
          ...(hotspots.length > 0
            ? [
                {
                  type: "list_card",
                  props: {
                    title: "Top emission sources",
                    items: hotspots.map((hotspot) => ({
                      label: hotspot.label,
                      value: `${formatDecimal(Number(hotspot.tons) || 0)} tCO2e (${formatDecimal(Number(hotspot.share) || 0)}%)`
                    }))
                  }
                }
              ]
            : [])
        ]
      }),
      showCompleteness: () => {
        const summary = surfaceContext.completenessSummary || {};
        const items = (summary.items || []).concat([
          {
            label: "Skipped sections",
            value: String(summary.skippedCount || 0)
          },
          {
            label: "Sections marked unknown",
            value: String(summary.unknownCount || 0)
          }
        ]);
        return {
          userText: "Show me what's missing from my data.",
          assistantText: `Here's your data completeness for ${activeYear}.`,
          cards: [
            {
              type: "list_card",
              props: {
                title: `Completeness — ${activeYear}`,
                items
              }
            }
          ]
        };
      }
    }),
    [
      availableYears,
      activeYear,
      surveyEmissions,
      hotspots,
      surfaceContext,
      triggerPdfReportDownload
    ]
  );

  const nextBestAction = useMemo(
    () => buildNextBestAction(surfaceContext),
    [surfaceContext]
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          gap: 2,
          height: { xs: "auto", md: "100%" }
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflowY: { xs: "visible", md: "auto" }
          }}
        >
          <DashboardCanvas
            activeTab={activeTab}
            surfaceContext={surfaceContext}
            surveyEmissions={surveyEmissions}
            nextBestAction={nextBestAction}
            topSupplierEmissions={topSupplierEmissions}
            totalSurveyTons={totalVendorSurveyTons}
            surveySupplierCount={surveySupplierCount}
            scopePieSeries={scopePieSeries}
            hotspots={hotspots}
            offices={offices}
            pdfReportDownload={pdfReportDownload}
            customers={customers}
          />
        </Box>
        <ScoutRail
          surface="dashboard"
          surfaceContext={surfaceContext}
          onClientAction={onClientAction}
          collapsed={railCollapsed}
          onToggleCollapse={() => setRailCollapsed((prev) => !prev)}
        />
      </Box>
      {pdfReportDownload.rendererJsx}
    </>
  );
};

export default Dashboard;
